/**
 * CS 前端商户信息共享缓存
 *
 * 解决多个页面重复调用 getmerchlist({ page: 1, page_size: 200 }) 的问题。
 *
 * 缓存层级：
 *   1. 模块级变量（内存）：同一次页面会话内，跨路由共享
 *   2. sessionStorage：浏览器本地缓存，刷新页面后仍可用，关闭标签页后清除
 *
 * 缓存 TTL：3 分钟
 *
 * 用法：
 *   import { getMerchantInfoMap, getMerchantDisplayName } from '@/utils/merchantCache'
 *
 *   // 获取商户信息 Map（自动使用缓存）
 *   const map = await getMerchantInfoMap()
 *   const info = map[deliverId]
 *
 *   // 获取商户显示名称（自动使用缓存）
 *   const name = await getMerchantDisplayName(deliverId, '默认名')
 */

import { getmerchlist, getmerchpayinfos } from '@/api/merch'

const CACHE_TTL = 3 * 60 * 1000 // 3 分钟
const STORAGE_KEY_LIST = 'cs_merch_list_cache'
const STORAGE_KEY_PAY = 'cs_merch_pay_cache_'

// ─── 模块级缓存 ───
let _merchList = null       // 商户列表原始数据
let _merchListTimestamp = 0  // 商户列表最后获取时间
let _merchInfoMap = null     // deliver_id → merchant info 的 Map

// 进件信息缓存：{ [deliverId]: { data, timestamp } }
const _payInfoCache = {}

// ─── 辅助函数 ───

function isCacheValid(timestamp) {
  return timestamp && Date.now() - timestamp < CACHE_TTL
}

function loadFromStorage(key) {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function saveToStorage(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    // sessionStorage 满了或不可用，忽略
  }
}

// ─── 核心函数 ───

/**
 * 获取商户列表（带缓存）
 * @param {boolean} forceRefresh - 强制刷新
 * @returns {Promise<Array>} 商户列表数组
 */
export async function fetchMerchantList(forceRefresh = false) {
  // 1. 先读内存缓存
  if (!forceRefresh && _merchList && isCacheValid(_merchListTimestamp)) {
    return _merchList
  }

  // 2. 读 sessionStorage 缓存
  if (!forceRefresh) {
    const stored = loadFromStorage(STORAGE_KEY_LIST)
    if (stored && stored.timestamp && isCacheValid(stored.timestamp)) {
      _merchList = stored.data
      _merchListTimestamp = stored.timestamp
      _merchInfoMap = null // 重置 map，下次构建
      return _merchList
    }
  }

  // 3. 请求 API
  const resp = await getmerchlist({ page: 1, page_size: 200 })
  if (resp.code === 0 && resp.desc && resp.desc.delivers) {
    // eslint-disable-next-line require-atomic-updates
    _merchList = resp.desc.delivers
    // eslint-disable-next-line require-atomic-updates
    _merchListTimestamp = Date.now()
    _merchInfoMap = null // 重置 map，下次构建
    saveToStorage(STORAGE_KEY_LIST, { data: _merchList, timestamp: _merchListTimestamp })
    return _merchList
  }

  // 请求失败时，如果有旧缓存就返回旧的
  if (_merchList) return _merchList
  return []
}

/**
 * 获取商户信息 Map（deliver_id → merchant info）
 * @param {boolean} forceRefresh
 * @returns {Promise<Object>} { [deliverId]: merchantInfo }
 */
export async function getMerchantInfoMap(forceRefresh = false) {
  if (!forceRefresh && _merchInfoMap) {
    return _merchInfoMap
  }

  const list = await fetchMerchantList(forceRefresh)
  // eslint-disable-next-line require-atomic-updates
  _merchInfoMap = {}
  list.forEach((m) => {
    const id = m.id || m.deliver_id
    if (id) _merchInfoMap[id] = m
  })
  return _merchInfoMap
}

/**
 * 获取指定商户的进件信息（带缓存）
 * @param {number} deliverId
 * @param {boolean} forceRefresh
 * @returns {Promise<Object|null>} 进件信息（含 account_holder, store_address 等）
 */
export async function fetchMerchantPayInfo(deliverId, forceRefresh = false) {
  const key = String(deliverId)

  // 1. 内存缓存
  if (!forceRefresh && _payInfoCache[key] && isCacheValid(_payInfoCache[key].timestamp)) {
    return _payInfoCache[key].data
  }

  // 2. sessionStorage
  if (!forceRefresh) {
    const stored = loadFromStorage(STORAGE_KEY_PAY + key)
    if (stored && stored.timestamp && isCacheValid(stored.timestamp)) {
      _payInfoCache[key] = stored
      return stored.data
    }
  }

  // 3. API
  try {
    const resp = await getmerchpayinfos({ deliver_id: deliverId })
    if (resp.code === 0 && Array.isArray(resp.desc) && resp.desc.length > 0) {
      // 按状态优先级选择最佳进件记录：approved > new/handling > declined
      // closed 状态的记录不作为候选项
      const statusPriority = { approved: 0, new: 1, handling: 2, declined: 3 }
      const candidates = resp.desc.filter(r => r.status !== 'closed')
      if (candidates.length === 0) {
        return null
      }
      candidates.sort((a, b) => {
        const pa = statusPriority[a.status] ?? 4
        const pb = statusPriority[b.status] ?? 4
        if (pa !== pb) return pa - pb
        // 同优先级的取最新提交的（created_date 降序）
        return (b.created_date || 0) - (a.created_date || 0)
      })
      const best = candidates[0]
      let parsed = {}
      if (best.pay_info) {
        try {
          parsed = JSON.parse(best.pay_info)
        } catch (e) {
          console.error('[merchantCache] parse pay_info error:', e)
        }
      }
      const result = {
        account_holder: parsed.bank_account_info?.account_holder || '',
        store_address: parsed.store_address || '',
        status: best.status || '',
        raw: best,
      }
      // eslint-disable-next-line require-atomic-updates
      _payInfoCache[key] = { data: result, timestamp: Date.now() }
      saveToStorage(STORAGE_KEY_PAY + key, _payInfoCache[key])
      return result
    }
  } catch (e) {
    console.error('[merchantCache] fetch pay info error:', e)
  }

  return null
}

/**
 * 获取商户显示名称（优先级：进件户名 → 昵称 → 商户名 → 默认值）
 *
 * @param {number} deliverId
 * @param {string} fallback - 所有来源都没有时的默认值
 * @param {Object} [knownInfo] - 已知商户信息（如从详情接口获取的），避免重复请求
 * @returns {Promise<string>}
 */
export async function getMerchantDisplayName(deliverId, fallback = '未知商户', knownInfo = null) {
  // 1. 从商户列表获取基础信息
  let info = knownInfo
  if (!info) {
    const map = await getMerchantInfoMap()
    info = map[deliverId] || {}
  }

  const nickName = info.nick_name || ''
  const name = info.name || ''
  const idStr = deliverId ? ('商家' + deliverId) : ''

  // 2. 从进件资料获取 account_holder
  let accountHolder = info.account_holder || ''
  if (!accountHolder) {
    try {
      const payInfo = await fetchMerchantPayInfo(deliverId)
      if (payInfo && payInfo.account_holder) {
        accountHolder = payInfo.account_holder
      }
    } catch (e) {
      // 忽略，走 fallback
    }
  }

  // 3. 按优先级返回
  return accountHolder || nickName || name || fallback || idStr
}

/**
 * 清除所有缓存（如商户信息变更后调用）
 */
export function clearMerchantCache() {
  _merchList = null
  _merchListTimestamp = 0
  _merchInfoMap = null
  Object.keys(_payInfoCache).forEach(k => delete _payInfoCache[k])

  // 清除 sessionStorage
  try {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('cs_merch_'))
      .forEach(k => sessionStorage.removeItem(k))
  } catch (e) {
    // ignore
  }
}
